import { useState, useContext } from "react";
import { useNavigate } from "react-router";
import axios from 'axios';

import { AppContext } from '../../context.js';

import { MoreVertical, Comment, ChevronDown, Refresh, ChevronUp, ArrowRight, CalendarEdit, Plus, CheckLarge, Minus, Close, UnfoldMore, Lock } from "../../files/icons/index.js";

import { Status, Button } from './component.js';
import Editor from "./editor.js";
import Drop from "../components/drop.js";
import Chat from "../components/chat.js";
import { DatePicker, DateSelector } from "../components/datePicker.js";
import RecurrencePicker from "../components/recurrencePicker.js";
import PhysicianPicker from "../components/physicianPicker.js";
import AssessmentFormModal from "../components/assessmentFormModal.js";
import GcodeSelectModal from "../components/gcodeSelectModal.js";
import CheckinModal from "./checkinModal.js";
import { ICD10_CODES, icd10Label } from "../components/icd10.js";
import { FullName } from '../../utils/string.js';
import { isCognitiveStagingTemplate, joinScores } from '../../utils/assessments.js';
import { getTaskCollapsed, setTaskCollapsed } from '../../utils/taskCollapse.js';

import { DateToFormat, DatetimeToDate, InRangeUTC, FirstDayOfMonth, LastDayOfMonth, formatDateYMD, parseDateYMD } from "../../utils/datetime.js";

// Mirrors projects.GCodeFromTemplateName (env/mongo/projects/model.go): extracts
// the GUIDE procedure code from a project template title, e.g. "Post-Alignment
// – G0520" -> "G0520". Returns "" if none is present.
function gcodeFromTemplateTitle(title) {
  const match = (title || '').match(/G05\d{2}/i);
  return match ? match[0].toUpperCase() : '';
}

const CLOSED_ASSESSMENT_FORM_MODAL = {
  visible: false,
  taskID: null,
  actionID: null,
  assessmentID: null,
  actionStatus: false,
  cognitiveStaging: false,
  template: null,
};

function TaskCard(props) {
  const context = useContext(AppContext);

  const { task: initialTask, users, settings, projectTemplate, projectID = null, onProjectAutoComplete = null, onPatientChange = null, patient = null, hasCognitiveStaging = false, onCognitiveStagingChange = null, onTaskDuplicated = null, onTaskDeleted = null, canReorder = false, isReorderDragging = false, isReorderDragOver = false, onReorderDragStart = null, onReorderDragOver = null, onReorderDrop = null, onReorderDragEnd = null, readOnly = false } = props;

  // Pre-Alignment-only action types (ICD-10, Alignment Date, Tier Assignment) are
  // gated on the project's template phase — not the patient's current lifecycle —
  // so they remain available when viewing a completed Pre-Alignment project after
  // the patient has moved into Post-Alignment.
  const isPreAlignmentProject = (() => {
    if (!projectTemplate?.id || !settings?.projectTemplatesGroups) return false;
    const group = settings.projectTemplatesGroups.find((g) => (g.templates || []).includes(projectTemplate.id));
    return group?.phase === 'PRE';
  })();

  const [task, setTask] = useState(initialTask);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [closeTaskStatusDrop, setCloseTaskStatusDrop] = useState(0);

  const [hoveredTaskAction, setHoveredTaskAction] = useState(null);
  const [dragActionID, setDragActionID] = useState(null);
  const [dragOverActionID, setDragOverActionID] = useState(null);

  const [closeAddActionDrop, setCloseAddActionDrop] = useState(0);
  const [assessmentTemplatePicker, setAssessmentTemplatePicker] = useState({ visible: false, taskID: null, actionID: null });
  const [assessmentFormModal, setAssessmentFormModal] = useState(CLOSED_ASSESSMENT_FORM_MODAL);

  const [cognitiveStagingPicker, setCognitiveStagingPicker] = useState({ visible: false, taskID: null, actionID: null });

  const [icd10CodePicker, setIcd10CodePicker] = useState({ visible: false, taskID: null, actionID: null, actionTitle: null, currentValue: null });
  const [icd10Search, setIcd10Search] = useState('');

  const [alignmentDatePicker, setAlignmentDatePicker] = useState({ visible: false, taskID: null, actionID: null, actionTitle: null, currentValue: null });

  const [tierAssignmentPicker, setTierAssignmentPicker] = useState({ visible: false, taskID: null, actionID: null, actionTitle: null, submitting: false });

  const [checkinModal, setCheckinModal] = useState({ visible: false, taskID: null, actionID: null, periodStart: null, periodEnd: null, busy: false });

  const [selectOptionsModal, setSelectOptionsModal] = useState({ visible: false, taskID: null, actionID: null, options: [] });

  const [changeTaskStatusModalVisible, setChangeTaskStatusModalVisible] = useState(false);
  const [changeTaskStatusModalData, setChangeTaskStatusModalData] = useState(null);
  const [changeTaskStatusSelectedReason, setChangeTaskStatusSelectedReason] = useState(null);
  const [changeTaskStatusChangeCaregiverReasonID, setChangeTaskStatusChangeCaregiverReasonID] = useState(null);
  const [changeTaskStatusChangeCaregiverReasonOther, setChangeTaskStatusChangeCaregiverReasonOther] = useState('');

  // Update task created at
  const [updateTaskCreatedAtData, setUpdateTaskCreatedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  // Update task closed at
  const [updateTaskClosedAtData, setUpdateTaskClosedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  const [duplicateTaskModalVisible, setDuplicateTaskModalVisible] = useState(false);
  const [deleteTaskModalVisible, setDeleteTaskModalVisible] = useState(false);
  const [deleteCognitiveStagingAction, setDeleteCognitiveStagingAction] = useState({ visible: false, taskID: null, action: null });

  const [expandedTasksChats, setExpandedTasksChats] = useState(() => initialTask.hasNotes ? [initialTask.id] : []);

  const [collapsed, setCollapsed] = useState(
    () => getTaskCollapsed(context.id, initialTask.id) ?? false
  );

  function toggleCollapsed() {
    setCollapsed((v) => {
      const next = !v;
      setTaskCollapsed(context.id, initialTask.id, next);
      return next;
    });
  }

  // Functions
  function changeTaskStatus(taskID, status) {
    if (status.reasons.length > 0) {
      setChangeTaskStatusModalData({status, taskID});
      setChangeTaskStatusSelectedReason(null);
      setChangeTaskStatusChangeCaregiverReasonID(null);
      setChangeTaskStatusChangeCaregiverReasonOther('');
      setChangeTaskStatusModalVisible(true);
      return;
    }

    updateTaskStatus(taskID, status.id, null, null, null);
  }

  function changeTaskStatusSelectReason(reason) {
    setChangeTaskStatusSelectedReason(reason);
    setChangeTaskStatusChangeCaregiverReasonID(null);
    setChangeTaskStatusChangeCaregiverReasonOther('');
    // If no sub-reasons, confirm immediately
    if (!reason.subReasons || reason.subReasons.length === 0) {
      setChangeTaskStatusModalVisible(false);
      updateTaskStatus(changeTaskStatusModalData.taskID, changeTaskStatusModalData.status.id, reason.id, null, null);
      setChangeTaskStatusModalData(null);
    }
  }

  function changeTaskStatusConfirm() {
    const { taskID, status } = changeTaskStatusModalData;
    const selectedSubReason = changeTaskStatusSelectedReason && changeTaskStatusSelectedReason.subReasons
      ? changeTaskStatusSelectedReason.subReasons.find(sr => sr.id === changeTaskStatusChangeCaregiverReasonID)
      : null;
    const isOther = selectedSubReason && selectedSubReason.title === 'Other (please specify)';
    setChangeTaskStatusModalVisible(false);
    setChangeTaskStatusModalData(null);
    updateTaskStatus(
      taskID,
      status.id,
      changeTaskStatusSelectedReason ? changeTaskStatusSelectedReason.id : null,
      changeTaskStatusChangeCaregiverReasonID,
      isOther ? changeTaskStatusChangeCaregiverReasonOther : null,
    );
  }

  function updateTaskStatus(taskID, statusID, reasonID, changeCaregiverReasonID, changeCaregiverReasonOther) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateStatus', {
      id: taskID,
      statusID: statusID,
      reasonID: reasonID,
      changeCaregiverReasonID: changeCaregiverReasonID,
      changeCaregiverReasonOther: changeCaregiverReasonOther,
    }).then((response) => {
      setTask({
        ...task,
        statusType: response.data.statusType,
        statusID: response.data.statusID,
        statusName: response.data.statusName,
        statusReasonID: response.data.statusReasonID,
        statusReasonName: response.data.statusReasonName,

        closedAt: response.data.closedAt,
        closedBy: response.data.closedBy,
        closedByName: response.data.closedByName,
      });
      setCloseTaskStatusDrop(prev => prev + 1);
      if ((response.data.statusType === 'COMPLETED' || response.data.statusType === 'CANCELED') && onProjectAutoComplete) {
        onProjectAutoComplete();
      }
      refreshMissingRequiredActionCount();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function changeTaskTitle(id, text) {
    setTask(prevTask => ({ ...prevTask, title: text }));
  }

  function keyUpTaskTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  function updateTaskTitle(id) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateTitle', {
      id: task.id,
      title: task.title
    }).then((response) => {

    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateTaskDescription(text, id) {
    if (text === "" || text === "<p><br></p>") {
      text = null;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateDescription', {
      id: task.id,
      description: text
    }).then((response) => {
      setTask(prevTask => ({ ...prevTask, description: text }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateTaskUser(user, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateUser', {
      id: id,
      userID: user.id
    }).then((response) => {
      setTask(prevTask => ({ ...prevTask, userID: user.id, userName: FullName(user.firstName, user.middleName, user.lastName) }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateTaskDeadline(date, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateDeadline', {
      id: id,
      deadline: date
    }).then((response) => {
      setTask(prevTask => ({ ...prevTask, deadlineAt: date }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function expandTaskChat(id) {
    // Open/close chat
    if (expandedTasksChats.includes(id)) {
      setExpandedTasksChats(prev => prev.filter((item) => item !== id));
    } else {
      setExpandedTasksChats(prev => [...prev, id]);
    }
  }

  function updateTaskRecurrence(taskID, intervalType, intervalDuration, fromType) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateRecurrence', {
      id: taskID,
      intervalType: intervalType,
      intervalDuration: intervalDuration,
      fromType: fromType,
    }).then((response) => {
      setTask(prevTask => ({ ...prevTask, recurreAt: response.data.recurreAt }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateTaskCreatedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateCreatedAt', {
      id: updateTaskCreatedAtData.id,
      createdAt: updateTaskCreatedAtData.date,
      reason: updateTaskCreatedAtData.reason,
    }).then((response) => {
      setTask(prevTask => ({ ...prevTask, createdAt: updateTaskCreatedAtData.date }));
      setUpdateTaskCreatedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateTaskClosedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateClosedAt', {
      id: updateTaskClosedAtData.id,
      closedAt: updateTaskClosedAtData.date,
      reason: updateTaskClosedAtData.reason,
    }).then((response) => {
      setTask(prevTask => ({ ...prevTask, closedAt: updateTaskClosedAtData.date }));
      setUpdateTaskClosedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openDuplicateTaskModal() {
    setCloseTaskStatusDrop(prev => prev + 1);
    setDuplicateTaskModalVisible(true);
  }

  function duplicateTaskConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/duplicate', {
      taskID: task.id,
    }).then((response) => {
      setDuplicateTaskModalVisible(false);
      if (onTaskDuplicated) {
        onTaskDuplicated(response.data);
      }
      refreshMissingRequiredActionCount();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openDeleteTaskModal() {
    setCloseTaskStatusDrop(prev => prev + 1);
    setDeleteTaskModalVisible(true);
  }

  function deleteTaskConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/delete', {
      id: task.id,
    }).then(() => {
      setDeleteTaskModalVisible(false);
      if (onTaskDeleted) {
        onTaskDeleted(task.id);
      }
      refreshMissingRequiredActionCount();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function changeTaskActionTitle(taskID, actionID, title) {
    setTask(prevTask => ({
      ...prevTask,
      actions: prevTask.actions.map((action) => action.id === actionID ? { ...action, title } : action)
    }));
  }

  function keyUpTaskActionTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  function refreshMissingRequiredActionCount() {
    if (!onPatientChange || !patient?.id) return;
    axios.post('/api/admin/patients', {
      filter: { id: [patient.id] },
      includeMissingRequired: true,
    }).then((response) => {
      const updated = response.data.patients?.[0];
      if (!updated) return;
      onPatientChange((prev) => ({
        ...prev,
        missingRequiredActionCount: updated.missingRequiredActionCount || 0,
      }));
    }).catch(() => {});
  }

  function updateTaskAction(taskID, actionID, title, status, value = undefined, onSuccess = null, options = undefined, required = undefined) {
    const existing = (task.actions || []).find((a) => a.id === actionID);
    const shouldRefreshBadge = required !== undefined || existing?.required;
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateAction', {
      id: taskID,
      actionID: actionID,
      title: title,
      status: status,
      value: value,
      ...(options !== undefined && { options }),
      ...(required !== undefined && { required }),
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: prevTask.actions.map((action) => action.id === actionID ? {
          ...action,
          title,
          status,
          ...(value !== undefined && { value }),
          ...(options !== undefined && { options }),
          ...(required !== undefined && { required }),
        } : action),
        // Always merge current status from response
        statusType: response.data.statusType,
        statusID: response.data.statusID,
        statusName: response.data.statusName,
        // Merge closed fields if task was auto-completed
        ...(response.data.closedAt !== undefined && {
          closedAt: response.data.closedAt,
          closedBy: response.data.closedBy,
          closedByName: response.data.closedByName,
        }),
      }));
      setCloseTaskStatusDrop(prev => prev + 1);
      if ((response.data.statusType === 'COMPLETED' || response.data.statusType === 'CANCELED') && onProjectAutoComplete) {
        onProjectAutoComplete();
      }
      if (onSuccess) {
        onSuccess();
      }
      if (shouldRefreshBadge) {
        refreshMissingRequiredActionCount();
      }
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function createTaskAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, response.data]
      }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openAssessmentTemplatePicker(taskID, actionID) {
    setAssessmentTemplatePicker({ visible: true, taskID, actionID });
  }

  // Creates a pending assessment action (no form yet). Completing happens later
  // via the action's button — same deferred pattern as monthly check-in.
  function createPendingAssessmentAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createAssessmentAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, {
          id: response.data.actionID,
          title: response.data.actionTitle,
          status: response.data.actionStatus,
          type: response.data.actionType,
          assessmentID: response.data.assessmentID || null,
        }]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function attachAssessmentToAction(taskID, actionID, templateID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createAssessmentAction', {
      id: taskID,
      actionID: actionID,
      templateID: templateID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: prevTask.actions.map((action) => action.id === actionID ? {
          ...action,
          title: response.data.actionTitle,
          status: response.data.actionStatus,
          type: response.data.actionType,
          assessmentID: response.data.assessmentID,
        } : action)
      }));
      setAssessmentTemplatePicker({ visible: false, taskID: null, actionID: null });
      setAssessmentFormModal({
        visible: true,
        taskID: taskID,
        actionID: actionID,
        assessmentID: response.data.assessmentID,
        actionStatus: response.data.actionStatus,
      });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openCognitiveStagingPicker(taskID, actionID) {
    setCognitiveStagingPicker({ visible: true, taskID, actionID });
  }

  function createPendingCognitiveStagingAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createCognitiveStagingAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, {
          id: response.data.actionID,
          title: response.data.actionTitle,
          status: response.data.actionStatus,
          type: response.data.actionType,
          assessmentID: response.data.assessmentID || null,
        }]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openCognitiveStagingForm(taskID, actionID, template) {
    setCognitiveStagingPicker({ visible: false, taskID: null, actionID: null });
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createCognitiveStagingAction', {
      id: taskID,
      actionID: actionID,
      templateID: template.id,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: prevTask.actions.map((action) => action.id === actionID ? {
          ...action,
          title: response.data.actionTitle,
          status: response.data.actionStatus,
          assessmentID: response.data.assessmentID,
        } : action),
      }));
      setAssessmentFormModal({
        visible: true,
        taskID,
        actionID,
        assessmentID: response.data.assessmentID,
        actionStatus: response.data.actionStatus,
        cognitiveStaging: true,
        template,
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openICD10CodePicker(taskID, actionID, actionTitle = null, currentValue = null) {
    setIcd10Search('');
    setIcd10CodePicker({ visible: true, taskID, actionID, actionTitle, currentValue });
  }

  // applyPrimaryDiagnosisToPatient mirrors the backend write in
  // service/admin/task/patient.go (applyPrimaryDiagnosis) into the parent's
  // patient state, so the ICD-10 add-action menu disables and the Patient Info
  // tab's icd10Code field reflects the code without a full refetch.
  function applyPrimaryDiagnosisToPatient(value) {
    if (!onPatientChange || !value) return;
    onPatientChange((prev) => {
      if (!prev) return prev;
      const fields = (prev.fields || []).map((f) =>
        f.code === 'icd10Code' ? { ...f, answers: [value] } : f
      );
      return {
        ...prev,
        claimInfo: { ...(prev.claimInfo || {}), primaryDiagnosis: value },
        fields,
      };
    });
  }

  function createICD10Action(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createICD10Action', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, response.data]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function selectICD10Code(code) {
    if (icd10CodePicker.actionID) {
      updateTaskAction(icd10CodePicker.taskID, icd10CodePicker.actionID, icd10CodePicker.actionTitle, true, code, () => applyPrimaryDiagnosisToPatient(code));
    }
    setIcd10CodePicker({ visible: false, taskID: null, actionID: null, actionTitle: null, currentValue: null });
  }

  function openAlignmentDatePicker(taskID, actionID, actionTitle = null, currentValue = null) {
    setAlignmentDatePicker({ visible: true, taskID, actionID, actionTitle, currentValue });
  }

  // applyAlignmentDateToPatient mirrors the backend write in service/admin/task/patient.go
  // (applyAlignmentDate) into the parent's patient state, so the Patient Info tab's
  // alignmentDate field reflects the date without a full patient refetch.
  function applyAlignmentDateToPatient(value) {
    if (!onPatientChange) return;
    onPatientChange((prev) => {
      if (!prev) return prev;
      const fields = (prev.fields || []).map((f) => f.code === 'alignmentDate' ? { ...f, answers: [value] } : f);
      const next = { ...prev, alignmentDate: value, fields };
      if (!prev.assessmentDate) {
        next.assessmentDate = value;
      }
      return next;
    });
  }

  function createAlignmentDateAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createAlignmentDateAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, response.data]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function selectAlignmentDate(date) {
    const value = formatDateYMD(date);
    if (alignmentDatePicker.actionID) {
      updateTaskAction(alignmentDatePicker.taskID, alignmentDatePicker.actionID, alignmentDatePicker.actionTitle, true, value, () => applyAlignmentDateToPatient(value));
    }
    setAlignmentDatePicker({ visible: false, taskID: null, actionID: null, actionTitle: null, currentValue: null });
  }

  function openTierAssignmentPicker(taskID, actionID, actionTitle = null) {
    setTierAssignmentPicker({ visible: true, taskID, actionID, actionTitle, submitting: false });
  }

  function closeTierAssignmentPicker() {
    setTierAssignmentPicker({ visible: false, taskID: null, actionID: null, actionTitle: null, submitting: false });
  }

  // Creates a pending Tier Assignment action. Completing (picking a tier and
  // starting Post-Alignment) happens later via the action button.
  function createPendingTierAssignmentAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createTierAssignmentAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, response.data]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // applyTierGCodeToPatient mirrors the backend write in
  // service/admin/task/patient.go (applyTierAssignment) into the parent's
  // patient state, so Post-Alignment gating and Patient Info tierAssignment
  // reflect the assigned tier without a full patient refetch.
  function applyTierGCodeToPatient(templateID) {
    if (!onPatientChange) return;
    const template = (settings.projectTemplates || []).find((t) => t.id === templateID);
    if (!template) return;
    const code = gcodeFromTemplateTitle(template.title);
    onPatientChange((prev) => {
      if (!prev) return prev;
      const fields = (prev.fields || []).map((f) =>
        f.code === 'tierAssignment' ? { ...f, answers: code ? [code] : f.answers } : f
      );
      return {
        ...prev,
        ...(code ? { procedureCode: code } : {}),
        fields,
      };
    });
  }

  // completeTierAssignmentAction finishes a pending Tier Assignment by
  // selecting a tier template. This only records the value and the implied
  // G-code — it does not start Post-Alignment (New); that's a separate,
  // explicitly confirmed action from the Post-Alignment tab.
  function completeTierAssignmentAction(taskID, actionID, actionTitle, templateID) {
    setTierAssignmentPicker(prev => ({ ...prev, submitting: true }));
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateAction', {
      id: taskID,
      actionID: actionID,
      title: actionTitle,
      status: true,
      value: templateID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: prevTask.actions.map((action) => action.id === actionID ? { ...action, title: actionTitle, status: true, value: templateID } : action),
        statusType: response.data.statusType,
        statusID: response.data.statusID,
        statusName: response.data.statusName,
        ...(response.data.closedAt !== undefined && {
          closedAt: response.data.closedAt,
          closedBy: response.data.closedBy,
          closedByName: response.data.closedByName,
        }),
      }));
      closeTierAssignmentPicker();
      applyTierGCodeToPatient(templateID);
      if ((response.data.statusType === 'COMPLETED' || response.data.statusType === 'CANCELED') && onProjectAutoComplete) {
        onProjectAutoComplete();
      }
    }).catch((e) => {
      context.addError(e);
      setTierAssignmentPicker(prev => ({ ...prev, submitting: false }));
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openCheckinModal(taskID, actionID, period = null) {
    const base = period ? new Date(period + "-01T00:00:00") : new Date();
    const periodStart = FirstDayOfMonth(base);
    const periodEnd = LastDayOfMonth(base);
    setCheckinModal({ visible: true, taskID, actionID, periodStart, periodEnd, busy: false });
  }

  function closeCheckinModal() {
    setCheckinModal({ visible: false, taskID: null, actionID: null, periodStart: null, periodEnd: null, busy: false });
  }

  function stepCheckinMonth(direction) {
    setCheckinModal(prev => {
      const base = direction < 0
        ? new Date(prev.periodStart.getFullYear(), prev.periodStart.getMonth() - 1, 1)
        : new Date(prev.periodStart.getFullYear(), prev.periodStart.getMonth() + 1, 1);
      return { ...prev, periodStart: FirstDayOfMonth(base), periodEnd: LastDayOfMonth(base) };
    });
  }

  function createLogMonthlyCheckinAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createLogMonthlyCheckinAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, response.data]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function createSelectAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createSelectAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, response.data]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function createTextAction(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/createTextAction', {
      id: taskID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: [...prevTask.actions, response.data]
      }));
      setCloseAddActionDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openSelectOptionsModal(taskID, actionID) {
    const action = (task.actions || []).find((a) => a.id === actionID);
    setSelectOptionsModal({
      visible: true,
      taskID,
      actionID,
      options: [...(action?.options || [])],
    });
  }

  function closeSelectOptionsModal() {
    setSelectOptionsModal({ visible: false, taskID: null, actionID: null, options: [] });
  }

  function changeModalSelectOption(index, text) {
    setSelectOptionsModal((prev) => {
      const options = [...prev.options];
      options[index] = text;
      return { ...prev, options };
    });
  }

  function addModalSelectOption() {
    setSelectOptionsModal((prev) => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  }

  function removeModalSelectOption(index) {
    setSelectOptionsModal((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  }

  function saveSelectOptionsModal() {
    const { taskID, actionID, options: raw } = selectOptionsModal;
    const action = (task.actions || []).find((a) => a.id === actionID);
    if (!action) {
      closeSelectOptionsModal();
      return;
    }
    const options = raw.map((o) => (o || '').trim()).filter((o) => o !== '');
    let value = action.value || '';
    let status = !!action.status;
    if (value && !options.includes(value)) {
      value = '';
      status = false;
    }
    updateTaskAction(taskID, actionID, action.title, status, value, () => closeSelectOptionsModal(), options);
  }

  // saveCheckinAction mirrors saveCheckin in projectTasks.js so the task action's
  // check-in modal writes through the same endpoint and keeps patient.monthlyCheckins
  // (and the header "Log monthly check-in" button) in sync.
  function saveCheckinAction(date) {
    if (!patient) return;
    setCheckinModal(prev => ({ ...prev, busy: true }));
    const period = DateToFormat(checkinModal.periodStart, "YYYY-MM");
    axios.post('/api/admin/claims/checkin', {
      patientID: patient.id,
      period,
      date: date || "",
    }).then(() => {
      if (onPatientChange) {
        onPatientChange((prev) => {
          if (!prev) return prev;
          const rest = (prev.monthlyCheckins || []).filter((ci) => ci.period !== period);
          const next = date ? [...rest, { period, date, createdByName: context.name }] : rest;
          return { ...prev, monthlyCheckins: next };
        });
      }
      if (checkinModal.taskID && checkinModal.actionID) {
        const action = (task.actions || []).find((a) => a.id === checkinModal.actionID);
        if (action) {
          updateTaskAction(checkinModal.taskID, checkinModal.actionID, action.title, !!date, date ? period : "");
        }
      }
      closeCheckinModal();
    }).catch((e) => {
      context.addError(e);
      setCheckinModal(prev => ({ ...prev, busy: false }));
    });
  }

  // Action types whose captured value lives on the patient (not just the task).
  // Deleting the action does not clear that value, so confirm before delete and
  // remind the user it can still be edited from the Patient Info tab.
  const PATIENT_INFO_ACTION_LABELS = {
    'ICD10': 'ICD-10 code',
    'ALIGNMENT_DATE': 'alignment date',
    'TIER_ASSIGNMENT': 'tier assignment (g-code)',
  };

  function deleteTaskAction(taskID, action) {
    if (action.type === 'COGNITIVE_STAGING' && action.status) {
      setDeleteCognitiveStagingAction({ visible: true, taskID, action });
      return;
    }

    const label = PATIENT_INFO_ACTION_LABELS[action.type];
    if (label && action.value) {
      const confirmed = window.confirm(
        `Delete this action?\n\nThe ${label} already recorded on the patient will not be cleared. You can still edit it in the Patient Info tab.`
      );
      if (!confirmed) return;
    }

    confirmDeleteTaskAction(taskID, action);
  }

  function confirmDeleteTaskAction(taskID, action) {
    const actionID = action.id;
    const actionType = action.type;
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/deleteAction', {
      id: taskID,
      actionID: actionID,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actions: prevTask.actions.filter((a) => a.id !== actionID)
      }));
      setDeleteCognitiveStagingAction({ visible: false, taskID: null, action: null });
      if (actionType === 'COGNITIVE_STAGING' && onCognitiveStagingChange) {
        onCognitiveStagingChange();
      }
      if (action.required && !action.status) {
        refreshMissingRequiredActionCount();
      }
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function reorderTaskActions(taskID, fromIndex, toIndex) {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;

    const previousActions = task.actions || [];
    const actions = [...previousActions];
    const [moved] = actions.splice(fromIndex, 1);
    actions.splice(toIndex, 0, moved);

    setTask((prevTask) => ({
      ...prevTask,
      actions,
    }));

    let u = context.addSpiner();
    axios.post('/api/admin/tasks/reorderActions', {
      id: taskID,
      actionIDs: actions.map((action) => action.id),
    }).catch((e) => {
      context.addError(e);
      setTask((prevTask) => ({
        ...prevTask,
        actions: previousActions,
      }));
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateTaskActionsAutocomplete(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateActionsAutocomplete', {
      id: taskID,
      actionsAutocomplete: !task.actionsAutocomplete,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        actionsAutocomplete: !prevTask.actionsAutocomplete
      }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateTaskRequiredComplete(taskID) {
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/updateRequiredComplete', {
      id: taskID,
      requiredComplete: !task.requiredComplete,
    }).then((response) => {
      setTask(prevTask => ({
        ...prevTask,
        requiredComplete: !prevTask.requiredComplete
      }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderTaskStatus(templateID, statusID) {
    const status = projectTemplate.taskTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!status) {
      return (
        <></>
      );
    }

    return (
      <Status color={status.color} muted={true} size={'small'} subtext={status.title} />
    );
  }

  function renderTaskNextStatuses(taskID, templateID, statusID) {
    const currentStatus = projectTemplate.taskTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!currentStatus) {
      return (
        <></>
      );
    }

    if (!currentStatus.next) {
      return (
        <div className="typo-medium-150">
          Task has no next statuses
        </div>
      );
    }

    return projectTemplate.taskTemplates.find((t) => t.id === templateID).statuses.filter((status) => currentStatus.next.includes(status.id)).map((status) => {
      return (
        <div key={status.id} className="side-item small typo-medium-150" onClick={() => changeTaskStatus(taskID, status)}>
          To <ArrowRight height={14} width={16} color={'grey'} /> {renderTaskStatus(templateID, status.id)}
        </div>
      );
    })
  }

  function renderTask() {

    var datePickerColor = "grey";
    if (task.deadlineAt && task.statusType !== "COMPLETED" && task.statusType !== "CANCELED") {
      const today = new Date();
      const deadlineDate = new Date(task.deadlineAt);

      // Helper functions for today's range
      const firstMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const lastMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

      // Helper functions for tomorrow's range
      const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      const firstMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0, 0, 0);
      const lastMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 23, 59, 59, 999);

      // Check if deadline is tomorrow (today is previous day of deadline)
      if (InRangeUTC(deadlineDate, firstMomentOfTomorrow, lastMomentOfTomorrow)) {
        datePickerColor = "yellow";
      }
      // Check if deadline is today or overdue
      else if (InRangeUTC(deadlineDate, firstMomentOfToday, lastMomentOfToday) || new Date().getTime() > deadlineDate.getTime()) {
        datePickerColor = "red";
      }
    }

    return (
      <div
        key={"task_" + task.id}
        id={"task_" + task.id}
        className="mb-2"
        style={{
          opacity: isReorderDragging ? 0.5 : 1,
          borderTop: isReorderDragOver && !isReorderDragging ? '2px solid #6c9bd1' : '2px solid transparent',
        }}
        onDragOver={(e) => {
          if (!canReorder || !onReorderDragOver) return;
          e.preventDefault();
          onReorderDragOver(task.id);
        }}
        onDrop={(e) => {
          if (!canReorder || !onReorderDrop) return;
          e.preventDefault();
          onReorderDrop(task.id);
        }}
      >
        <div className="card">
          <div className="card-header sm">
            <div className="d-flex justify-content-between align-items-center">
              {canReorder && (
                <div
                  className="item rect me-1"
                  style={{ cursor: 'grab' }}
                  draggable={true}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    if (onReorderDragStart) onReorderDragStart(task.id);
                  }}
                  onDragEnd={() => {
                    if (onReorderDragEnd) onReorderDragEnd();
                  }}
                  title="Drag to reorder"
                >
                  <UnfoldMore height={16} width={16} color={'grey'} />
                </div>
              )}
              <div className="item rect me-1" style={{ cursor: "pointer" }} onClick={toggleCollapsed}>
                {collapsed
                  ? <ChevronDown height={16} width={16} color={'grey'} />
                  : <ChevronUp height={16} width={16} color={'grey'} />}
              </div>
              <input type="text" className="input borderless typo-strong-200 w-100" placeholder="Title..." value={task.title} onChange={(e) => changeTaskTitle(task.id, e.target.value)} onBlur={() => updateTaskTitle(task.id)} onKeyUp={(e) => keyUpTaskTitle(e)} />
              <div className="d-flex justify-content-end align-items-center w-25">
                {renderTaskStatus(task.templateID, task.statusID)}
                {!readOnly && (
                <Drop close={closeTaskStatusDrop} side={"right"} className="ms-1">
                  <div className="item rect">
                    <MoreVertical height={14} width={16} color={'grey'} />
                  </div>
                  <div className="card" style={{width: 200}}>
                    <div className="card-header sm">
                      <div className="typo-normal-150 text-muted">Change status:</div>
                      {renderTaskNextStatuses(task.id, task.templateID, task.statusID)}
                      <div className="typo-normal-150 text-muted">Change dates:</div>
                      <div className="side-item small typo-medium-150" onClick={() => setUpdateTaskCreatedAtData({ visible: true, id: task.id, date: task.createdAt, reason: null })}>
                        Change created at date
                      </div>
                      {task.closedAt && (
                        <div className="side-item small typo-medium-150" onClick={() => setUpdateTaskClosedAtData({ visible: true, id: task.id, date: task.closedAt, reason: null })}>
                          Change closed at date
                        </div>
                      )}
                      <div className="typo-normal-150 text-muted">Duplicate:</div>
                      <div className="side-item small typo-medium-150" onClick={openDuplicateTaskModal}>
                        Duplicate task
                      </div>
                      <div className="typo-normal-150 text-muted">Delete:</div>
                      <div className="side-item small typo-medium-150 text-danger" onClick={openDeleteTaskModal}>
                        Delete task
                      </div>
                    </div>
                  </div>
                </Drop>
                )}
              </div>
            </div>

            {!collapsed && (
            <>
            <Editor
              placeholder="Task description..."
              value={task.description}
              onChangeID={task.id}
              onBlur={updateTaskDescription}
              toolbar="limited"
              className="text-editor borderless"
            />

            <div className="mt-1">
              {task.actions && task.actions.map((action, ai) => {
                const isAssessment = action.type === 'ASSESSMENT';
                const isICD10 = action.type === 'ICD10';
                const isAlignmentDate = action.type === 'ALIGNMENT_DATE';
                const isTierAssignment = action.type === 'TIER_ASSIGNMENT';
                const isLogMonthlyCheckin = action.type === 'LOG_MONTHLY_CHECKIN';
                const isCognitiveStaging = action.type === 'COGNITIVE_STAGING';
                const isSelect = action.type === 'SELECT';
                const isText = action.type === 'TEXT';
                const isSpecialized = isAssessment || isICD10 || isAlignmentDate || isTierAssignment || isLogMonthlyCheckin || isCognitiveStaging || isSelect;
                const canReorderActions = !readOnly && task.actions.length > 1;
                const actionLocked = readOnly;
                const missingRequired = action.required && !action.status;
                return (
                  <div
                    key={"task_"+task.id+"_action_" + action.id}
                    className={"item d-flex justify-content-start align-items-center" + (missingRequired ? " red" : "")}
                    style={{
                      opacity: dragActionID === action.id ? 0.5 : 1,
                      borderTop: dragOverActionID === action.id && dragActionID !== action.id ? '2px solid #6c9bd1' : '2px solid transparent',
                    }}
                    onMouseEnter={() => setHoveredTaskAction(action.id)}
                    onMouseLeave={() => setHoveredTaskAction(null)}
                    onDragOver={(e) => {
                      if (!canReorderActions || !dragActionID) return;
                      e.preventDefault();
                      if (dragOverActionID !== action.id) {
                        setDragOverActionID(action.id);
                      }
                    }}
                    onDrop={(e) => {
                      if (!canReorderActions || !dragActionID) return;
                      e.preventDefault();
                      const fromIndex = task.actions.findIndex((a) => a.id === dragActionID);
                      const toIndex = ai;
                      setDragActionID(null);
                      setDragOverActionID(null);
                      reorderTaskActions(task.id, fromIndex, toIndex);
                    }}
                  >
                    {canReorderActions && (
                      <div
                        className="d-flex justify-content-start align-items-center me-1"
                        style={{cursor: 'grab', opacity: hoveredTaskAction === action.id || dragActionID === action.id ? 1 : 0.35}}
                        draggable={true}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          setDragActionID(action.id);
                        }}
                        onDragEnd={() => {
                          setDragActionID(null);
                          setDragOverActionID(null);
                        }}
                        title="Drag to reorder"
                      >
                        <UnfoldMore height={14} width={14} color={'grey'} />
                      </div>
                    )}
                    <div
                      className="d-flex justify-content-start align-items-center"
                      style={{cursor: (isSpecialized || actionLocked) ? (actionLocked ? 'not-allowed' : 'default') : 'pointer', opacity: actionLocked ? 0.7 : 1}}
                      onClick={(isSpecialized || actionLocked) ? undefined : () => updateTaskAction(task.id, action.id, action.title, !action.status)}
                    >
                      {action.status ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={missingRequired ? 'firebrick' : 'lightgrey'} />}
                    </div>
                    {isTierAssignment ? (
                      <div className="d-flex align-items-center w-100">
                        <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                        <div
                          className="typo-normal-150 ms-2"
                          style={{cursor: (actionLocked || action.value) ? (actionLocked ? 'not-allowed' : 'default') : 'pointer', textDecoration: (actionLocked || action.value) ? 'none' : 'underline', whiteSpace: 'nowrap', opacity: actionLocked ? 0.7 : 1}}
                          onClick={(actionLocked || action.value) ? undefined : () => openTierAssignmentPicker(task.id, action.id, action.title)}
                        >
                          {action.value
                            ? (((settings.projectTemplates || []).find((t) => t.id === action.value) || {}).title || action.value)
                            : "Click to assign tier"}
                        </div>
                      </div>
                    ) : isAssessment ? (
                      <div className="d-flex align-items-center w-100">
                        <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                        <div
                          className="typo-normal-150 ms-2"
                          style={{cursor: actionLocked ? 'not-allowed' : 'pointer', textDecoration: actionLocked ? 'none' : 'underline', whiteSpace: 'nowrap', opacity: actionLocked ? 0.7 : 1}}
                          onClick={actionLocked ? undefined : () => {
                            if (action.assessmentID) {
                              setAssessmentFormModal({ visible: true, taskID: task.id, actionID: action.id, assessmentID: action.assessmentID, actionStatus: action.status });
                            } else {
                              openAssessmentTemplatePicker(task.id, action.id);
                            }
                          }}
                        >
                          {action.assessmentID
                            ? (action.status ? "Open assessment (completed)" : "Click to open assessment")
                            : "Click to choose assessment"}
                        </div>
                      </div>
                    ) : isCognitiveStaging ? (
                      <div className="d-flex align-items-center w-100">
                        <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                        <div
                          className="typo-normal-150 ms-2"
                          style={{cursor: actionLocked ? 'not-allowed' : 'pointer', textDecoration: actionLocked ? 'none' : 'underline', whiteSpace: 'nowrap', opacity: actionLocked ? 0.7 : 1}}
                          onClick={actionLocked ? undefined : () => {
                            if (action.assessmentID) {
                              setAssessmentFormModal({ visible: true, taskID: task.id, actionID: action.id, assessmentID: action.assessmentID, actionStatus: action.status, cognitiveStaging: true });
                            } else {
                              openCognitiveStagingPicker(task.id, action.id);
                            }
                          }}
                        >
                          {action.assessmentID
                            ? (action.value ? `Staging: ${action.value} (click to change)` : (action.status ? "Open staging (completed)" : "Click to open staging"))
                            : "Click to choose staging tool"}
                        </div>
                      </div>
                    ) : isICD10 ? (
                      <div className="d-flex align-items-center w-100">
                        <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                        <div
                          className="typo-normal-150 ms-2"
                          style={{cursor: actionLocked ? 'not-allowed' : 'pointer', textDecoration: actionLocked ? 'none' : 'underline', whiteSpace: 'nowrap', opacity: actionLocked ? 0.7 : 1}}
                          onClick={actionLocked ? undefined : () => openICD10CodePicker(task.id, action.id, action.title, action.value)}
                        >
                          {action.value ? `${icd10Label(action.value)} (click to change)` : "Click to select ICD-10 code"}
                        </div>
                      </div>
                    ) : isAlignmentDate ? (
                      <div className="d-flex align-items-center w-100">
                        <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                        <div
                          className="typo-normal-150 ms-2"
                          style={{cursor: actionLocked ? 'not-allowed' : 'pointer', textDecoration: actionLocked ? 'none' : 'underline', whiteSpace: 'nowrap', opacity: actionLocked ? 0.7 : 1}}
                          onClick={actionLocked ? undefined : () => openAlignmentDatePicker(task.id, action.id, action.title, action.value)}
                        >
                          {action.value ? `${DateToFormat(parseDateYMD(action.value), context.dateFormat)} (click to change)` : "Click to set alignment date"}
                        </div>
                      </div>
                    ) : isLogMonthlyCheckin ? (() => {
                      const checkinPeriod = action.value;
                      const existingCheckin = checkinPeriod ? (patient?.monthlyCheckins || []).find((ci) => ci.period === checkinPeriod && ci.date) : null;
                      return (
                        <div className="d-flex align-items-center w-100">
                          <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                          <div
                            className="typo-normal-150 ms-2"
                            style={{cursor: actionLocked ? 'not-allowed' : 'pointer', textDecoration: actionLocked ? 'none' : 'underline', whiteSpace: 'nowrap', opacity: actionLocked ? 0.7 : 1}}
                            onClick={actionLocked ? undefined : () => openCheckinModal(task.id, action.id, checkinPeriod)}
                          >
                            {existingCheckin ? `Checked in at ${existingCheckin.date} (click to change)` : "Click to log monthly check-in"}
                          </div>
                        </div>
                      );
                    })() : isSelect ? (
                      <div className="d-flex align-items-center w-100">
                        <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                        <select
                          className="input sm typo-normal-150 ms-2"
                          style={{minWidth: 140, maxWidth: 220, opacity: actionLocked ? 0.7 : 1}}
                          value={action.value || ''}
                          disabled={actionLocked}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateTaskAction(task.id, action.id, action.title, !!value, value);
                          }}
                        >
                          <option value="">Select...</option>
                          {(action.options || []).filter((o) => (o || '').trim() !== '').map((option, oi) => (
                            <option key={"select_opt_"+action.id+"_"+oi} value={option}>{option}</option>
                          ))}
                        </select>
                        {!actionLocked && (
                        <div
                          className="typo-normal-150 ms-2"
                          style={{cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap'}}
                          onClick={() => openSelectOptionsModal(task.id, action.id)}
                        >
                          Configure
                        </div>
                        )}
                      </div>
                    ) : isText ? (
                      <div className="d-flex align-items-center w-100">
                        <input type="text" className="input borderless typo-normal-150" style={{backgroundColor: "transparent"}} size={Math.max((action.title || '').length, 15)} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                        <input
                          type="text"
                          className="input sm typo-normal-150 ms-2"
                          style={{minWidth: 140, maxWidth: 320, opacity: actionLocked ? 0.7 : 1}}
                          placeholder="Enter value..."
                          defaultValue={action.value || ''}
                          disabled={actionLocked}
                          readOnly={actionLocked}
                          onBlur={(e) => {
                            if (actionLocked) return;
                            const value = e.target.value;
                            updateTaskAction(task.id, action.id, action.title, action.status, value);
                          }}
                        />
                      </div>
                    ) : (
                      <input type="text" className="input borderless typo-normal-150 w-100" style={{backgroundColor: "transparent"}} placeholder="Action title..." value={action.title} onChange={(e) => changeTaskActionTitle(task.id, action.id, e.target.value)} onBlur={() => updateTaskAction(task.id, action.id, action.title, action.status)} onKeyUp={(e) => keyUpTaskActionTitle(e)} readOnly={actionLocked} disabled={actionLocked} />
                    )}
                    {actionLocked && action.required && (
                      <div className={"typo-normal-125 " + (missingRequired ? "text-red" : "text-muted")} style={{whiteSpace: 'nowrap', opacity: 0.7}}>Required</div>
                    )}
                    {!actionLocked && hoveredTaskAction === action.id && (
                      <div
                        className="d-flex justify-content-start align-items-center me-1"
                        style={{cursor: 'pointer', whiteSpace: 'nowrap'}}
                        onClick={() => updateTaskAction(task.id, action.id, action.title, action.status, undefined, null, undefined, !action.required)}
                      >
                        {action.required ? <CheckLarge height={16} width={16} color={missingRequired ? 'firebrick' : 'darkseagreen'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
                        <div className={"typo-normal-125 " + (missingRequired ? "text-red" : "text-muted")}>Required {action.required ? "on" : "off"}</div>
                      </div>
                    )}
                    {!actionLocked && hoveredTaskAction !== action.id && action.required && (
                      <div className={"typo-normal-125 " + (missingRequired ? "text-red" : "text-muted")} style={{whiteSpace: 'nowrap'}}>Required</div>
                    )}
                    {!actionLocked && hoveredTaskAction === action.id && (
                      <div className="d-flex justify-content-start align-items-center" style={{cursor: 'pointer'}} onClick={() => deleteTaskAction(task.id, action)}>
                        <Close height={16} width={16} color={'firebrick'} />
                      </div>
                    )}
                  </div>
                );
              })}
              {task.actions && (
                <div className="d-flex justify-content-start align-items-center mt-2">
                  {!(readOnly || patient?.statusType === "ARCHIVED") ? (
                  <Drop close={closeAddActionDrop} side={"left"}>
                    <div className="item">
                      <Plus height={16} width={16} color={'lightgrey'} />
                      <div className="typo-normal-125 text-muted">Add action</div>
                    </div>
                    <div className="card shadowmore" style={{width: 'fit-content'}}>
                      <div className="card-header sm" style={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0}}>
                        <div className="item" style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0}} onClick={() => { createTaskAction(task.id); setCloseAddActionDrop(prev => prev + 1); }}>
                          <div className="typo-strong-175">Checkbox</div>
                          <div className="typo-normal-150">Plain checklist item</div>
                        </div>
                        <div className="item" style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0}} onClick={() => createSelectAction(task.id)}>
                          <div className="typo-strong-175">Select</div>
                          <div className="typo-normal-150">Dropdown with custom options</div>
                        </div>
                        <div className="item" style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0}} onClick={() => createTextAction(task.id)}>
                          <div className="typo-strong-175">Text</div>
                          <div className="typo-normal-150">Free-text value</div>
                        </div>
                        <div className="item" style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0}} onClick={() => createPendingAssessmentAction(task.id)}>
                          <div className="typo-strong-175">Assessment</div>
                          <div className="typo-normal-150">Attach a full assessment form</div>
                        </div>
                        {isPreAlignmentProject && (
                          <div
                            className="item"
                            style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0, opacity: patient?.claimInfo?.primaryDiagnosis ? 0.5 : 1, cursor: patient?.claimInfo?.primaryDiagnosis ? 'not-allowed' : 'pointer'}}
                            onClick={patient?.claimInfo?.primaryDiagnosis ? undefined : () => createICD10Action(task.id)}
                          >
                            <div className="typo-strong-175">ICD-10 Code</div>
                            <div className="typo-normal-150">{patient?.claimInfo?.primaryDiagnosis ? "Patient already has a primary diagnosis — edit the existing one" : "Select a diagnosis code"}</div>
                          </div>
                        )}
                        {isPreAlignmentProject && (
                          <div
                            className="item"
                            style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0, opacity: patient?.alignmentDate ? 0.5 : 1, cursor: patient?.alignmentDate ? 'not-allowed' : 'pointer'}}
                            onClick={patient?.alignmentDate ? undefined : () => createAlignmentDateAction(task.id)}
                          >
                            <div className="typo-strong-175">Alignment Date</div>
                            <div className="typo-normal-150">{patient?.alignmentDate ? "Patient already has an alignment date — edit the existing one" : "Set the patient's alignment date"}</div>
                          </div>
                        )}
                        <div className="item" style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0}} onClick={() => createLogMonthlyCheckinAction(task.id)}>
                          <div className="typo-strong-175">Log Monthly Check-In</div>
                          <div className="typo-normal-150">Record the patient's monthly check-in</div>
                        </div>
                        <div
                          className="item"
                          style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0}}
                          onClick={() => createPendingCognitiveStagingAction(task.id)}
                        >
                          <div className="typo-strong-175">Cognitive Staging</div>
                          <div className="typo-normal-150">{hasCognitiveStaging ? "Updates the patient's current FAST/CDR score when completed; the prior result stays on record" : "Run a FAST or CDR staging assessment"}</div>
                        </div>
                        {isPreAlignmentProject && (
                          <div
                            className="item"
                            style={{minWidth: 240, whiteSpace: 'nowrap', justifyContent: 'initial', flexDirection: 'column', alignItems: 'flex-start', gap: 0}}
                            onClick={() => createPendingTierAssignmentAction(task.id)}
                          >
                            <div className="typo-strong-175">Tier Assignment</div>
                            <div className="typo-normal-150">Assign the patient's tier (G-code)</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Drop>
                  ) : (
                    <div className="item disabled" style={{opacity: 0.5, cursor: 'not-allowed'}}>
                      <Lock height={16} width={16} color={'lightgrey'} />
                      <div className="typo-normal-125 text-muted">Add action</div>
                    </div>
                  )}

                  {task.actions.length > 0 && (
                    <div className="item ms-2" style={readOnly ? { opacity: 0.7, cursor: 'not-allowed' } : undefined} onClick={readOnly ? undefined : () => updateTaskActionsAutocomplete(task.id)}>
                      {task.actionsAutocomplete ? <CheckLarge height={16} width={16} color={'darkseagreen'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
                      <div className="typo-normal-125 text-muted">Autocomplete turned {task.actionsAutocomplete ? "on" : "off"}</div>
                    </div>
                  )}

                  <div className="item ms-2" style={readOnly ? { opacity: 0.7, cursor: 'not-allowed' } : undefined} onClick={readOnly ? undefined : () => updateTaskRequiredComplete(task.id)}>
                    {task.requiredComplete ? <CheckLarge height={16} width={16} color={'darkseagreen'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
                    <div className="typo-normal-125 text-muted">Required complete {task.requiredComplete ? "on" : "off"}</div>
                  </div>
                </div>
              )}
            </div>

          <div className="d-flex justify-content-start align-items-center mt-2">
            <div className="d-flex justify-content-start align-items-center me-3">
              <span className="typo-normal-150">created at {DateToFormat(DatetimeToDate(task.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(task.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
            </div>

            {task.closedAt && (
              <div className="d-flex justify-content-start align-items-center me-3">
                <span className="typo-normal-150">closed at {DateToFormat(DatetimeToDate(task.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(task.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="d-flex justify-content-start align-items-center w-100">
              <PhysicianPicker
                physicianList={users}
                physicianID={task.userID}
                onChange={updateTaskUser}
                onChangeID={task.id}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className="me-2"
                disabled={readOnly}
              />

              <DatePicker
                date={task.deadlineAt}
                onChange={updateTaskDeadline}
                onChangeID={task.id}
                leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className={"me-2 " + datePickerColor}
                disabled={readOnly}
              />

              <RecurrencePicker
                recurreAt={task.recurreAt}
                onChange={updateTaskRecurrence}
                onChangeID={task.id}
                leftIcon={<Refresh height={16} width={16} color={'grey'} />}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className="me-2"
                disabled={readOnly}
              />
            </div>
            <div className="d-flex justify-content-end align-items-bottom">
              <div onClick={() => expandTaskChat(task.id)}>
                {expandedTasksChats.includes(task.id) ? (
                  <div className="item grey">
                    <Comment height={16} width={16} color={'grey'} />
                    <span className="typo-normal-150">Notes</span>
                    <ChevronUp height={14} width={16} color={'grey'} />
                  </div>
                ) : (
                  <div className="item grey">
                    <Comment height={16} width={16} color={'grey'} />
                    <span className="typo-normal-150">Notes</span>
                    <ChevronDown height={14} width={16} color={'grey'} />
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
          )}
        </div>
          {!collapsed && expandedTasksChats.includes(task.id) && (
            <div className="card-content sm">
              <Chat
                projectID={projectID}
                taskID={task.id}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <></>
    );
  }

  return (
    <>
      {updateTaskCreatedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateTaskCreatedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Task Creation Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of creation and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateTaskCreatedAtData.date}
                        onChange={(date) => setUpdateTaskCreatedAtData({...updateTaskCreatedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />

                      <input type="text" className="input borderless typo-normal-150 w-100 ms-2" placeholder="Reason..." value={updateTaskCreatedAtData.reason} onChange={(e) => setUpdateTaskCreatedAtData({...updateTaskCreatedAtData, reason: e.target.value})} />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateTaskCreatedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateTaskClosedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateTaskClosedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Task Completion Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of completion and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateTaskClosedAtData.date}
                        onChange={(date) => setUpdateTaskClosedAtData({...updateTaskClosedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />

                      <input type="text" className="input borderless typo-normal-150 w-100 ms-2" placeholder="Reason..." value={updateTaskClosedAtData.reason} onChange={(e) => setUpdateTaskClosedAtData({...updateTaskClosedAtData, reason: e.target.value})} />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateTaskClosedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {duplicateTaskModalVisible && (
        <div className="fixed-bottom modal-bg" onClick={() => setDuplicateTaskModalVisible(false)}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Duplicate Task</span><br/>
                    <span className="typo-normal-175">Checkbox, select, text, and monthly check-in actions will be duplicated. Special actions such as assessments, ICD-10, alignment date, tier assignment, and cognitive staging will not be copied.</span>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={() => setDuplicateTaskModalVisible(false)} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={duplicateTaskConfirm} color="blue" text="Duplicate" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTaskModalVisible && (
        <div className="fixed-bottom modal-bg" onClick={() => setDeleteTaskModalVisible(false)}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Delete Task</span><br/>
                    <span className="typo-normal-175">This permanently deletes this task and its actions. This cannot be undone. Any patient-side values already recorded (e.g. ICD-10, alignment date, tier) will not be reverted.</span>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={() => setDeleteTaskModalVisible(false)} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={deleteTaskConfirm} color="red" text="Delete Task" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteCognitiveStagingAction.visible && (
        <div className="fixed-bottom modal-bg" onClick={() => setDeleteCognitiveStagingAction({ visible: false, taskID: null, action: null })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Delete Cognitive Staging</span><br/>
                    <span className="typo-normal-175">This permanently deletes this Cognitive Staging action and its FAST/CDR assessment from the Assessments tab. This cannot be undone.</span>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={() => setDeleteCognitiveStagingAction({ visible: false, taskID: null, action: null })} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={() => confirmDeleteTaskAction(deleteCognitiveStagingAction.taskID, deleteCognitiveStagingAction.action)} color="red" text="Delete" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectOptionsModal.visible && (
        <div className="fixed-bottom modal-bg" onClick={closeSelectOptionsModal}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Configure select options</span><br/>
                    <span className="typo-normal-175">Add, edit, or remove the choices for this dropdown</span>
                  </div>
                  <div className="card-content sm">
                    {selectOptionsModal.options.map((option, oi) => (
                      <div key={"select_modal_opt_"+oi} className="d-flex align-items-center mb-2">
                        <input
                          type="text"
                          className="input typo-normal-150 w-100"
                          placeholder="Option..."
                          value={option}
                          onChange={(e) => changeModalSelectOption(oi, e.target.value)}
                          onKeyUp={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          autoFocus={oi === selectOptionsModal.options.length - 1 && option === ''}
                        />
                        <div className="d-flex align-items-center ms-2" style={{cursor: 'pointer'}} onClick={() => removeModalSelectOption(oi)} title="Remove option">
                          <Close height={16} width={16} color={'firebrick'} />
                        </div>
                      </div>
                    ))}
                    <div className="item" style={{padding: 0}} onClick={addModalSelectOption}>
                      <Plus height={16} width={16} color={'lightgrey'} />
                      <div className="typo-normal-150 text-muted">Add option</div>
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={closeSelectOptionsModal} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={saveSelectOptionsModal} color="blue" text="Save" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {changeTaskStatusModalVisible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setChangeTaskStatusModalVisible(false)}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Change Status</span><br/>
                    <span className="typo-normal-175">
                      {changeTaskStatusSelectedReason && changeTaskStatusSelectedReason.subReasons && changeTaskStatusSelectedReason.subReasons.length > 0
                        ? 'Select a sub-reason'
                        : 'Select a reason for changing the status'}
                    </span>
                  </div>
                  <div className="card-content">
                    {/* Step 1: reason list (hidden once a reason with sub-reasons is selected) */}
                    {(!changeTaskStatusSelectedReason || !changeTaskStatusSelectedReason.subReasons || changeTaskStatusSelectedReason.subReasons.length === 0) && changeTaskStatusModalData && changeTaskStatusModalData.status.reasons.map((reason) => {
                      return (
                        <div
                          key={reason.id}
                          className="side-item small typo-medium-150 mt-3"
                          onClick={() => changeTaskStatusSelectReason(reason)}
                        >
                   
                          {reason.title}
                        </div>
                      );
                    })}

                    {/* Step 2: sub-reason list */}
                    {changeTaskStatusSelectedReason && changeTaskStatusSelectedReason.subReasons && changeTaskStatusSelectedReason.subReasons.length > 0 && (
                      <>
                        {changeTaskStatusSelectedReason.subReasons.map((sr) => (
                          <div
                            key={sr.id}
                            className={`side-item small typo-medium-150${changeTaskStatusChangeCaregiverReasonID === sr.id ? ' active' : ''}`}
                            onClick={() => { setChangeTaskStatusChangeCaregiverReasonID(sr.id); setChangeTaskStatusChangeCaregiverReasonOther(''); }}
                          >
                            {sr.title}
                          </div>
                        ))}

                        {/* Free-text for "Other (please specify)" */}
                        {changeTaskStatusChangeCaregiverReasonID && changeTaskStatusSelectedReason.subReasons.find(sr => sr.id === changeTaskStatusChangeCaregiverReasonID)?.title === 'Other (please specify)' && (
                          <div className="mt-2">
                            <input
                              type="text"
                              className="input borderless typo-normal-150 w-100"
                              placeholder="Please specify..."
                              value={changeTaskStatusChangeCaregiverReasonOther}
                              onChange={(e) => setChangeTaskStatusChangeCaregiverReasonOther(e.target.value)}
                              autoFocus
                            />
                          </div>
                        )}

                        <div className="card-content text-end mt-2">
                          <Button
                            onClick={changeTaskStatusConfirm}
                            color="blue"
                            text="Confirm"
                            disabled={
                              !changeTaskStatusChangeCaregiverReasonID ||
                              (changeTaskStatusSelectedReason.subReasons.find(sr => sr.id === changeTaskStatusChangeCaregiverReasonID)?.title === 'Other (please specify)' && !changeTaskStatusChangeCaregiverReasonOther.trim())
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {assessmentTemplatePicker.visible && (
        <div className="fixed-bottom modal-bg" onClick={() => setAssessmentTemplatePicker({ visible: false, taskID: null, actionID: null })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Choose assessment</span><br/>
                    <span className="typo-normal-175">Select a template to attach to this task</span>
                  </div>
                  <div className="card-content sm">
                    {settings && settings.assessmentTemplates && settings.assessmentTemplates.filter((template) => !isCognitiveStagingTemplate(template.id)).sort((a, b) => a.title.localeCompare(b.title)).map((template) => {
                      return (
                        <div key={template.id} className="item" style={{minWidth: 260}} onClick={() => attachAssessmentToAction(assessmentTemplatePicker.taskID, assessmentTemplatePicker.actionID, template.id)}>
                          <div className="w-100">
                            <div className="typo-strong-175">{template.title}</div>
                            <div className="typo-normal-150">{template.description}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {cognitiveStagingPicker.visible && (
        <div className="fixed-bottom modal-bg" onClick={() => setCognitiveStagingPicker({ visible: false, taskID: null, actionID: null })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" style={{width: 'fit-content', margin: '0 auto'}} onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Choose staging tool</span><br/>
                    <span className="typo-normal-175">Select FAST or CDR to run a cognitive staging assessment</span>
                  </div>
                  <div className="card-content sm">
                    {(settings.assessmentTemplates || []).filter((t) => isCognitiveStagingTemplate(t.id)).sort((a, b) => a.title.localeCompare(b.title)).map((t) => (
                      <div key={t.id} className="item" style={{minWidth: 260}} onClick={() => openCognitiveStagingForm(cognitiveStagingPicker.taskID, cognitiveStagingPicker.actionID, t)}>
                        <div className="w-100">
                          <div className="typo-strong-175">{t.title}</div>
                          <div className="typo-normal-150">{t.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {alignmentDatePicker.visible && (
        <div className="fixed-bottom modal-bg" onClick={() => setAlignmentDatePicker({ visible: false, taskID: null, actionID: null, actionTitle: null, currentValue: null })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" style={{width: 'fit-content', margin: '0 auto'}} onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Select alignment date</span><br/>
                    <span className="typo-normal-175">Choose the date this patient entered Post-Alignment</span>
                  </div>
                  <div className="card-content sm">
                    <DateSelector
                      date={alignmentDatePicker.currentValue ? parseDateYMD(alignmentDatePicker.currentValue) : null}
                      dateSelected={selectAlignmentDate}
                      maxDate={new Date()}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tierAssignmentPicker.visible && (() => {
        // Post-Alignment (New) only — EST conversion is the month-7 / convertToEst
        // path, and applyTierAssignment rejects POST_EST templates.
        const templates = settings.projectTemplates || [];
        const groups = settings.projectTemplatesGroups || [];
        const tierTemplates = [];
        for (const phase of ['POST_NEW']) {
          const group = groups.find((g) => g.phase === phase);
          if (!group) continue;
          for (const id of group.templates || []) {
            const t = templates.find((x) => x.id === id);
            if (t) tierTemplates.push(t);
          }
        }
        return (
          <GcodeSelectModal
            patient={patient}
            templates={tierTemplates}
            submitting={tierAssignmentPicker.submitting}
            onClose={tierAssignmentPicker.submitting ? () => {} : closeTierAssignmentPicker}
            onConfirm={(templateID) => completeTierAssignmentAction(tierAssignmentPicker.taskID, tierAssignmentPicker.actionID, tierAssignmentPicker.actionTitle, templateID)}
            title="Assign tier"
            subtitle="Select the patient's tier (G-code) received from CMS."
            confirmText="Assign Tier"
          />
        );
      })()}

      {checkinModal.visible && patient && (() => {
        const period = DateToFormat(checkinModal.periodStart, "YYYY-MM");
        const existing = (patient.monthlyCheckins || []).find((ci) => ci.period === period);
        const checkinClaimLike = {
          firstName: patient.firstName,
          lastName: patient.lastName || "",
          monthlyCheckinComplete: !!existing,
          monthlyCheckinDate: existing?.date || "",
        };
        return (
          <CheckinModal
            claim={checkinClaimLike}
            periodStart={checkinModal.periodStart}
            periodEnd={checkinModal.periodEnd}
            periodLabel={DateToFormat(checkinModal.periodStart, "MF YYYY")}
            submitting={checkinModal.busy}
            onPrevMonth={() => stepCheckinMonth(-1)}
            onNextMonth={() => stepCheckinMonth(1)}
            onClose={() => { if (!checkinModal.busy) closeCheckinModal(); }}
            onConfirm={(date) => saveCheckinAction(date)}
            onClear={() => saveCheckinAction("")}
          />
        );
      })()}

      {icd10CodePicker.visible && (
        <div className="fixed-bottom modal-bg" onClick={() => setIcd10CodePicker({ visible: false, taskID: null, actionID: null, actionTitle: null, currentValue: null })}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Select ICD-10 code</span><br/>
                    <span className="typo-normal-175">Choose the dementia diagnosis code for this task</span>
                    <input
                      type="text"
                      className="input borderless typo-normal-150 w-100 mt-2"
                      placeholder="Search by code or description..."
                      value={icd10Search}
                      onChange={(e) => setIcd10Search(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="card-content sm" style={{maxHeight: 400, overflowY: 'auto'}}>
                    {ICD10_CODES
                      .filter((c) => {
                        const q = icd10Search.trim().toLowerCase();
                        if (!q) return true;
                        return c.code.toLowerCase().includes(q) || c.definition.toLowerCase().includes(q);
                      })
                      .map((c) => (
                        <div
                          key={c.code}
                          className={`item${icd10CodePicker.currentValue === c.code ? ' active' : ''}`}
                          style={{minWidth: 260}}
                          onClick={() => selectICD10Code(c.code)}
                        >
                          <div className="w-100">
                            <div className="typo-strong-175">{c.code}</div>
                            <div className="typo-normal-150">{c.definition}</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {assessmentFormModal.visible && (
        <AssessmentFormModal
          assessmentID={assessmentFormModal.assessmentID}
          actionStatus={assessmentFormModal.actionStatus}
          template={assessmentFormModal.template}
          patientID={patient && patient.id}
          users={users}
          settings={settings}
          cognitiveStaging={assessmentFormModal.cognitiveStaging}
          onCognitiveStagingChange={onCognitiveStagingChange}
          onClose={() => setAssessmentFormModal(CLOSED_ASSESSMENT_FORM_MODAL)}
          onSaved={(updatedAssessment) => {
            if (assessmentFormModal.cognitiveStaging) {
              const score = joinScores(updatedAssessment.scores);
              setTask(prevTask => ({
                ...prevTask,
                actions: prevTask.actions.map((action) => action.id === assessmentFormModal.actionID ? {
                  ...action,
                  assessmentID: updatedAssessment.id,
                } : action),
              }));
              updateTaskAction(assessmentFormModal.taskID, assessmentFormModal.actionID, "Complete " + updatedAssessment.title, true, score);
              if (onCognitiveStagingChange) onCognitiveStagingChange();
            } else {
              updateTaskAction(assessmentFormModal.taskID, assessmentFormModal.actionID, "Complete " + updatedAssessment.title, true);
            }
            setAssessmentFormModal(CLOSED_ASSESSMENT_FORM_MODAL);
          }}
        />
      )}

      {renderTask()}
    </>
  );
}

export default TaskCard;
